import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    AlertTriangle,
    Award,
    BarChart3,
    CheckCircle2,
    Clock3,
    Filter,
    Gauge,
    RefreshCcw,
    TrendingUp,
    Users,
    X,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import { API_BASE_URL, getRequest } from "../../../../api";
import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { fetchSkillEvalsByCampagneRequest } from "../../../redux/actions/skillEvaluationActions";
import "./DashboardSkillsRespo.css";

const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    grey: "#757575",
};

const CHART_COLORS = [
    COLORS.primary,
    COLORS.secondary,
    COLORS.success,
    COLORS.warning,
    COLORS.info,
    "#9c27b0",
    "#00bcd4",
];
const DOMAIN_SCORE_COLORS = [COLORS.primary, COLORS.secondary];
const REPARTITION_COLORS = ["#4f7df3", "#48b887", COLORS.secondary, COLORS.primary, COLORS.warning, COLORS.info];

const STATUS_META = {
    NOT_STARTED: { label: "Non demarree", color: COLORS.secondary },
    EN_ATTENTE_COLLAB: { label: "Attente collaborateur", color: COLORS.warning },
    EN_ATTENTE_MANAGER: { label: "Attente manager", color: COLORS.secondary },
    EN_ATTENTE_N2: { label: "Attente N+2", color: COLORS.info },
    VALIDEE: { label: "Validee", color: COLORS.primary },
    REJETEE: { label: "Rejetee", color: COLORS.error },
    PROPOSE: { label: "Proposee", color: COLORS.grey },
};

const numberOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const pct = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
};

const round1 = (value) => Math.round(value * 10) / 10;

const statusCode = (statut) => String(typeof statut === "string" ? statut : statut?.name || "").toUpperCase();

const average = (values) => {
    const nums = (values || []).map(numberOrNull).filter((n) => n !== null);
    if (!nums.length) return null;
    return round1(nums.reduce((sum, n) => sum + n, 0) / nums.length);
};

const getSkillScore = (niveau) =>
    numberOrNull(niveau?.scoreObtenu) ??
    numberOrNull(niveau?.scoreN2) ??
    numberOrNull(niveau?.scoreManager) ??
    numberOrNull(niveau?.scoreCollab);

const getExpectedScore = (niveau) =>
    numberOrNull(niveau?.niveauRequis) ??
    numberOrNull(niveau?.scoreRequis) ??
    numberOrNull(niveau?.niveauAttendu) ??
    numberOrNull(niveau?.scoreAttendu);

const getUserName = (evaluation) =>
    evaluation?.userFullName ||
    evaluation?.nom ||
    evaluation?.fullName ||
    `Collaborateur ${evaluation?.userId || evaluation?.id || ""}`.trim();

function SectionTitle({ icon, children }) {
    return (
        <div className="dsrSectionTitle">
            <span className="dsrSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function StatCard({ title, value, subtitle, icon, color, onClick }) {
    return (
        <div
            className={`dsrStatCard${onClick ? " dsrStatCard--clickable" : ""}`}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(event) => {
                if (onClick && (event.key === "Enter" || event.key === " ")) onClick();
            }}
        >
            <div className="dsrStatCard__row">
                <div>
                    <div className="dsrStatCard__title">{title}</div>
                    <div className="dsrStatCard__value" style={{ color }}>{value}</div>
                    {subtitle ? <div className="dsrStatCard__sub">{subtitle}</div> : null}
                </div>
                <div
                    className="dsrStatIcon"
                    style={{
                        color,
                        background: `${color}15`,
                        borderColor: `${color}25`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function DetailsModal({ title, subtitle, children, onClose }) {
    return (
        <div className="dsrModalBackdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="dsrModalPanel">
                <div className="dsrModalHeader">
                    <div>
                        <div className="dsrModalTitle">{title}</div>
                        {subtitle ? <div className="dsrModalSubtitle">{subtitle}</div> : null}
                    </div>
                    <button className="dsrModalClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="dsrModalBody">{children}</div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="dsrChartCard">
            <div className="dsrChartCard__title">{title}</div>
            <div className="dsrChartCard__body">{children}</div>
        </div>
    );
}

function ProgressPanel({ title, rows }) {
    return (
        <div className="dsrPanel">
            <div className="dsrPanel__title">{title}</div>
            <div className="dsrPanel__body">
                {rows.length ? (
                    <div className="dsrProgressList">
                        {rows.map((row, index) => (
                            <div className="dsrProgressRow" key={row.name}>
                                <div className="dsrProgressLabel" title={row.name}>{row.name}</div>
                                <div className="dsrProgressTrack">
                                    <div
                                        className="dsrProgressFill"
                                        style={{
                                            width: `${Math.min(row.value, 100)}%`,
                                            background: CHART_COLORS[index % CHART_COLORS.length],
                                        }}
                                    />
                                </div>
                                <div className="dsrProgressValue">{row.value}%</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="dsrEmpty">Aucune donnee disponible</div>
                )}
            </div>
        </div>
    );
}

export default function DashboardSkillsRespo() {
    const dispatch = useDispatch();
    const campagnesState = useSelector((state) => state.campagnes || {});
    const skillState = useSelector((state) => state.skillEvaluation || {});
    const campagnesRaw = campagnesState.list || [];
    const evaluations = skillState.skillEvaluations || [];
    const loading = !!skillState.loading;

    const [campagneId, setCampagneId] = useState("");
    const [participation, setParticipation] = useState(null);
    const [participationLoading, setParticipationLoading] = useState(false);
    const [detailsModal, setDetailsModal] = useState(null);

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const campagnes = useMemo(() => {
        return (campagnesRaw || [])
            .filter((c) => String(c?.typeCampagne || "").toUpperCase() === "SKILLS" || !c?.typeCampagne)
            .sort((a, b) => (a?.status === b?.status ? 0 : a?.status === true ? -1 : 1));
    }, [campagnesRaw]);

    useEffect(() => {
        if (!campagnes.length) {
            setCampagneId("");
            return;
        }
        if (!campagneId || !campagnes.some((c) => String(c.id) === String(campagneId))) {
            const defaultCampagne = campagnes.find((c) => c?.isDefault) || campagnes.find((c) => c?.status === true) || campagnes[0];
            setCampagneId(String(defaultCampagne?.id || ""));
        }
    }, [campagnes, campagneId]);

    const refreshData = useCallback(() => {
        if (!campagneId) return;
        dispatch(fetchSkillEvalsByCampagneRequest(Number(campagneId)));
    }, [dispatch, campagneId]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        let cancelled = false;
        const loadParticipation = async () => {
            if (!campagneId) {
                setParticipation(null);
                return;
            }
            setParticipationLoading(true);
            try {
                const resp = await getRequest({
                    url: `${API_BASE_URL}/api/skill-evaluations/campagne/${campagneId}/participation`,
                    token: sessionStorage.getItem("token"),
                });
                if (!cancelled) setParticipation(resp.data);
            } catch {
                if (!cancelled) setParticipation(null);
            } finally {
                if (!cancelled) setParticipationLoading(false);
            }
        };
        loadParticipation();
        return () => {
            cancelled = true;
        };
    }, [campagneId]);

    const analytics = useMemo(() => {
        const total = evaluations.length;
        const statusCounts = evaluations.reduce((acc, evaluation) => {
            const code = statusCode(evaluation.statut) || "INCONNU";
            acc[code] = (acc[code] || 0) + 1;
            return acc;
        }, {});

        const allLevels = evaluations.flatMap((evaluation) =>
            (evaluation.niveaux || []).map((niveau) => ({ ...niveau, evaluation }))
        );
        const scoredLevels = allLevels
            .map((item) => ({
                ...item,
                score: getSkillScore(item),
                expected: getExpectedScore(item),
            }))
            .filter((item) => item.score !== null);

        const avgScore = average(scoredLevels.map((item) => item.score));
        const gaps = scoredLevels
            .filter((item) => item.expected !== null)
            .map((item) => ({ ...item, gap: item.expected - item.score }));
        const avgGap = average(gaps.map((item) => item.gap));
        const criticalGaps = gaps.filter((item) => item.gap >= 1).length;
        const criticalGapDetails = gaps
            .filter((item) => item.gap >= 1)
            .map((item) => ({
                collaborator: getUserName(item.evaluation),
                job: item.evaluation?.jobName || item.evaluation?.poste || item.evaluation?.userEmployeeCode || "-",
                skill: item.skillNom || item.skillCode || "Competence",
                domain: item.domaineNom || item.domaineName || "-",
                expected: item.expected,
                score: item.score,
                gap: item.gap,
                status: statusCode(item.evaluation?.statut),
            }))
            .sort((a, b) => b.gap - a.gap || a.collaborator.localeCompare(b.collaborator));

        const statusData = Object.entries(statusCounts).map(([code, value]) => ({
            name: STATUS_META[code]?.label || code,
            value,
            color: STATUS_META[code]?.color || COLORS.grey,
        }));

        const byDomain = Object.values(
            scoredLevels.reduce((acc, item) => {
                const name = item.domaineNom || item.domaineName || "Non classe";
                if (!acc[name]) acc[name] = { name, scores: [], gaps: [] };
                acc[name].scores.push(item.score);
                if (item.expected !== null) acc[name].gaps.push(item.expected - item.score);
                return acc;
            }, {})
        )
            .map((item) => ({
                name: item.name,
                score: average(item.scores) || 0,
                gap: average(item.gaps) || 0,
            }))
            .sort((a, b) => b.score - a.score)
            .slice(0, 8);

        const bySkill = Object.values(
            scoredLevels.reduce((acc, item) => {
                const key = item.skillId || item.skillNom || item.skillCode;
                const name = item.skillNom || item.skillCode || "Competence";
                if (!acc[key]) acc[key] = { name, scores: [], gaps: [], count: 0 };
                acc[key].scores.push(item.score);
                if (item.expected !== null) acc[key].gaps.push(item.expected - item.score);
                acc[key].count += 1;
                return acc;
            }, {})
        )
            .map((item) => ({
                name: item.name,
                score: average(item.scores) || 0,
                gap: average(item.gaps) || 0,
                count: item.count,
            }))
            .sort((a, b) => b.gap - a.gap || a.score - b.score);

        const topCriticalSkills = bySkill.filter((item) => item.gap > 0).slice(0, 5);
        const topStrongSkills = [...bySkill].sort((a, b) => b.score - a.score).slice(0, 5);

        const entityRows = Object.values(
            evaluations.reduce((acc, evaluation) => {
                const name = evaluation.entityName || evaluation.entiteName || "Non renseignee";
                if (!acc[name]) acc[name] = { name, total: 0, validated: 0 };
                acc[name].total += 1;
                if (statusCode(evaluation.statut) === "VALIDEE") acc[name].validated += 1;
                return acc;
            }, {})
        )
            .map((item) => ({ name: item.name, value: pct(item.validated, item.total) }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);

        const collaboratorRisks = evaluations
            .map((evaluation) => {
                const levels = (evaluation.niveaux || [])
                    .map((niveau) => ({
                        score: getSkillScore(niveau),
                        expected: getExpectedScore(niveau),
                    }))
                    .filter((niveau) => niveau.score !== null);
                const evalGaps = levels
                    .filter((niveau) => niveau.expected !== null)
                    .map((niveau) => niveau.expected - niveau.score);
                return {
                    name: getUserName(evaluation),
                    job: evaluation.jobName || evaluation.poste || evaluation.userEmployeeCode || "Poste non renseigne",
                    score: average(levels.map((niveau) => niveau.score)),
                    gap: average(evalGaps) || 0,
                    criticalCount: evalGaps.filter((gap) => gap >= 1).length,
                    status: statusCode(evaluation.statut),
                };
            })
            .filter((item) => item.score !== null)
            .sort((a, b) => b.gap - a.gap || b.criticalCount - a.criticalCount)
            .slice(0, 6);

        return {
            total,
            validated: statusCounts.VALIDEE || 0,
            pendingManager: statusCounts.EN_ATTENTE_MANAGER || 0,
            pendingN2: statusCounts.EN_ATTENTE_N2 || 0,
            pendingCollab: statusCounts.EN_ATTENTE_COLLAB || 0,
            completionRate: pct(statusCounts.VALIDEE || 0, total),
            avgScore,
            avgGap,
            criticalGaps,
            criticalGapDetails,
            statusData,
            byDomain,
            topCriticalSkills,
            topStrongSkills,
            entityRows,
            collaboratorRisks,
        };
    }, [evaluations]);

    const participationRate = participation?.participationRate ?? participation?.tauxParticipation ?? pct(participation?.responded || analytics.total, participation?.total || analytics.total);
    const participationTotal = participation?.total || analytics.total;
    const participationDone = participation?.responded || participation?.participants || analytics.total;

    const workflowTrend = useMemo(() => [
        { step: "Lancee", value: analytics.total },
        { step: "Auto-eval", value: Math.max(analytics.total - analytics.pendingCollab, 0) },
        { step: "Manager", value: Math.max(analytics.total - analytics.pendingCollab - analytics.pendingManager, 0) },
        { step: "N+2", value: Math.max(analytics.validated + analytics.pendingN2, 0) },
        { step: "Validee", value: analytics.validated },
    ], [analytics]);

    return (
        <div className="dsrPage">
            <header className="dsrHeader">
                <div>
                    <h1>Tableau de bord</h1>
                </div>

                <div className="dsrActions">
                    <div className="dsrSelectWrap">
                        <Filter size={15} />
                        <label htmlFor="dsr-campagne">Campagne</label>
                        <select
                            id="dsr-campagne"
                            className="dsrSelect"
                            value={campagneId}
                            onChange={(event) => setCampagneId(event.target.value)}
                        >
                            <option value="">Selectionnez une campagne</option>
                            {campagnes.map((campagne) => (
                                <option key={campagne.id} value={campagne.id}>
                                    {campagne.nom || campagne.name || `Campagne ${campagne.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            <main className="dsrMain">
                <SectionTitle icon={<Gauge size={18} />}>Pilotage global</SectionTitle>

                <div className="dsrGrid dsrGrid--4">
                    <StatCard
                        title="Collaborateurs évalués"
                        value={analytics.total}
                        icon={<Users size={20} />}
                        color={COLORS.primary}
                    />
                    <StatCard
                        title="Taux de participation"
                        value={`${participationRate || 0}%`}
                        icon={<TrendingUp size={20} />}
                        color={COLORS.success}
                    />
                    <StatCard
                        title="Écarts critiques"
                        value={analytics.criticalGaps}
                        icon={<AlertTriangle size={20} />}
                        color={COLORS.error}
                        onClick={() => setDetailsModal("critical-gaps")}
                    />
                    <StatCard
                        title="Score moyen"
                        value={analytics.avgScore ?? "-"}
                        icon={<Award size={20} />}
                        color={COLORS.secondary}
                    />
                </div>

                <SectionTitle icon={<BarChart3 size={18} />}>Analyse des résultats</SectionTitle>

                <div className="dsrGrid dsrGrid--2">
                    <ChartCard title="Répartition des statuts">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={analytics.statusData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={92}
                                    label={({ name, value }) => `${name}: ${value}`}
                                >
                                    {analytics.statusData.map((entry, index) => (
                                        <Cell key={entry.name} fill={REPARTITION_COLORS[index % REPARTITION_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Score moyen par domaine">
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={analytics.byDomain}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 4]} />
                                <RechartsTooltip />
                                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                                    {analytics.byDomain.map((entry, index) => (
                                        <Cell key={entry.name} fill={DOMAIN_SCORE_COLORS[index % DOMAIN_SCORE_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                <SectionTitle icon={<AlertTriangle size={18} />}>Priorités RH</SectionTitle>

                <div className="dsrGrid dsrGrid--3">
                    <ProgressPanel title="Taux de validation par entite" rows={analytics.entityRows} />

                    <div className="dsrPanel">
                        <div className="dsrPanel__title">Compétences à renforcer</div>
                        <div className="dsrPanel__body">
                            {analytics.topCriticalSkills.length ? (
                                <div className="dsrRiskList">
                                    {analytics.topCriticalSkills.map((skill) => (
                                        <div className="dsrRiskItem" key={skill.name}>
                                            <div>
                                                <div className="dsrRiskTitle">{skill.name}</div>
                                                <div className="dsrRiskMeta">{skill.count} evaluations · score moyen {skill.score}</div>
                                            </div>
                                            <span className="dsrBadge dsrBadge--danger">+{skill.gap}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="dsrEmpty">Aucun écart prioritaire</div>
                            )}
                        </div>
                    </div>

                    <div className="dsrPanel">
                        <div className="dsrPanel__title">Points forts collectifs</div>
                        <div className="dsrPanel__body">
                            {analytics.topStrongSkills.length ? (
                                <div className="dsrRiskList">
                                    {analytics.topStrongSkills.map((skill) => (
                                        <div className="dsrRiskItem" key={skill.name}>
                                            <div>
                                                <div className="dsrRiskTitle">{skill.name}</div>
                                                <div className="dsrRiskMeta">{skill.count} évaluations · écart moyen {skill.gap}</div>
                                            </div>
                                            <span className="dsrBadge dsrBadge--success">{skill.score}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="dsrEmpty">Aucune donnee disponible</div>
                            )}
                        </div>
                    </div>
                </div>

                {/*<div className="dsrPanel">*/}
                {/*    <div className="dsrPanel__title">Collaborateurs avec écarts prioritaires</div>*/}
                {/*    <div className="dsrTableWrap">*/}
                {/*        {analytics.collaboratorRisks.length ? (*/}
                {/*            <table className="dsrTable">*/}
                {/*                <thead>*/}
                {/*                <tr>*/}
                {/*                    <th>Collaborateur</th>*/}
                {/*                    <th>Poste / reference</th>*/}
                {/*                    <th>Statut</th>*/}
                {/*                    <th>Score moyen</th>*/}
                {/*                    <th>Ecart moyen</th>*/}
                {/*                    <th>Ecarts critiques</th>*/}
                {/*                </tr>*/}
                {/*                </thead>*/}
                {/*                <tbody>*/}
                {/*                {analytics.collaboratorRisks.map((row) => (*/}
                {/*                    <tr key={`${row.name}-${row.job}`}>*/}
                {/*                        <td>{row.name}</td>*/}
                {/*                        <td>{row.job}</td>*/}
                {/*                        <td>{STATUS_META[row.status]?.label || row.status || "-"}</td>*/}
                {/*                        <td>{row.score}</td>*/}
                {/*                        <td>*/}
                {/*                            <span className={`dsrBadge ${row.gap >= 1 ? "dsrBadge--danger" : "dsrBadge--warning"}`}>*/}
                {/*                                {row.gap}*/}
                {/*                            </span>*/}
                {/*                        </td>*/}
                {/*                        <td>{row.criticalCount}</td>*/}
                {/*                    </tr>*/}
                {/*                ))}*/}
                {/*                </tbody>*/}
                {/*            </table>*/}
                {/*        ) : (*/}
                {/*            <div className="dsrEmpty">Aucun collaborateur a afficher</div>*/}
                {/*        )}*/}
                {/*    </div>*/}
                {/*</div>*/}
            </main>

            {detailsModal === "critical-gaps" && (
                <DetailsModal
                    title="Détails des écarts critiques"
                    subtitle={`${analytics.criticalGapDetails.length} compétence(s) avec un écart supérieur ou égal a 1 niveau`}
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.criticalGapDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Competence</th>
                                    <th>Domaine</th>
                                    <th>Requis</th>
                                    <th>Obtenu</th>
                                    <th>Écart</th>
                                    <th>Statut</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.criticalGapDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${row.skill}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{row.skill}</td>
                                        <td>{row.domain}</td>
                                        <td>{row.expected}</td>
                                        <td>{row.score}</td>
                                        <td><span className="dsrBadge dsrBadge--danger">+{row.gap}</span></td>
                                        <td>{STATUS_META[row.status]?.label || row.status || "-"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucun écart critique pour cette campagne.</div>
                    )}
                </DetailsModal>
            )}
        </div>
    );
}
